import { IFormAction, IFormActions } from '../../@types/entityTypes';
import React, { ComponentProps, ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import { BasicHeader } from '../BasicHeader';
import { ModalBottomButtonContainer } from '../../components/modals/Styles';
import { Button, SuccessButton } from '../../components/connectedForm/Button';
import { default as Form } from '../../components/connectedForm';
import BlurTextField from '../../components/connectedForm/BlurTextField';
import {
  AssemblyStatus,
  FilterOperation,
  IAssembly,
  IAssemblyCompleteMutationResult,
  IAssemblyCompleteMutationVariables,
  IAssemblyReceiptInput,
  IAssemblyReceiptResponseQueryResult,
  IAssemblyReceiptResponseQueryVariables,
  IAssemblyReceiveMutationResult,
  IAssemblyReceiveMutationVariables,
  IBinLocation,
  IBoxLabelInstructionInput,
  IGoodsReceiptInput,
  ILotAttributeValue,
  IProduct,
  IProductQueryResult,
  IProductQueryVariables,
  Maybe,
  ReportType,
  Scalars,
} from '../../graphql/schema/p4types';
import { BlurTextFieldType } from '../../components/connectedForm/BlurTextField/BlurTextField';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import { BinLocation, GoodsReceiptLineLabel, Product } from '../../graphql/entities';
import { AttributeArray } from '../../components/connectedForm/inputComponents';
import { useLazyQuery, useQuery } from '@apollo/client';
import { IJobMeta, useBackgroundMutation } from '../../utils/BackgroundJobs';
import AssemblyReceiveMutation from '../../graphql/mutations/product/AssemblyReceiveMutation.graphql';
import AssemblyCompleteMutation from '../../graphql/mutations/product/AssemblyCompleteMutation.graphql';
import getClientMutationId from '../../utils/getClientMutationId';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import EditableDataGrid from '../../components/EditableDataGrid';
import DropdownEditor from '../../components/DropdownEditor';
import { IDispatchBoxLabelInstructionValues } from '../../views/sales/manualDispatchForm/IDispatchBoxLabelInstruction';
import AssemblyReceipt from '../../graphql/entities/product/AssemblyReceipt';
import ModalContext from '../../components/Context/ModalContext';
import DocumentModal2 from '../../components/modals/DocumentModal2';
import { Toggle } from '@fluentui/react/lib/Toggle';
import SupplierReturnStockAdjustmentModal from '../../components/modals/SupplierReturnStockAdjustmentModal';
import { ICommit } from '../../@types/editableDataGridTypes';
import FluentModal from '../../components/modals/FluentModal';
import { usePerformSave } from '../../components/connectedForm/Hooks/usePerformSave';
import { AssemblyPrintSource, useP4DocumentController } from '../../components/document/DocumentProvider';

const { receiptResponse } = AssemblyReceipt;

interface ReceiptLabelModalProps {
  isOpen: boolean;
}

const ReceiptLabelModal = ({ isOpen, ...props }: ReceiptLabelModalProps & ComponentProps<typeof DocumentModal2>) => {
  return <DocumentModal2 {...props} modalOpen={isOpen} key={'receiptLabels'} entity={GoodsReceiptLineLabel} />;
};

export enum AssemblyFormAction {
  ReserveComponents = 'RESERVE_COMPONENTS',
  ReceiveAssembly = 'RECEIVE_ASSEMBLY',
  PrintPickingList = 'PRINT_ASSEMBLY_PICK',
  CompleteAssembly = 'COMPLETE_ASSEMBLY',
  PrintDynamicReport = 'PRINT_DYNAMIC_REPORT',
  AssignToSalesOrder = 'ASSEMBLY_ASSIGNMENT',
}

export const assemblyFormActionsForPermissions: IFormActions = [
  {
    actionReference: AssemblyFormAction.ReserveComponents,
  },
  {
    actionReference: AssemblyFormAction.ReceiveAssembly,
  },
  {
    actionReference: AssemblyFormAction.PrintPickingList,
  },
  {
    actionReference: AssemblyFormAction.CompleteAssembly,
  },
  {
    actionReference: AssemblyFormAction.PrintDynamicReport,
  },
  {
    actionReference: AssemblyFormAction.AssignToSalesOrder,
  },
];

type ILotInfo = {
  lotAttributeConfigTitle: Maybe<Scalars['String']>;
  lotAttributeValue: Maybe<Scalars['String']>;
  product: Maybe<IProduct>;
};

type IAllocationLookup = {
  id: Maybe<Scalars['Int']>;
  lotInfo?: Maybe<ILotInfo>;
  lotAttributeTitle: Maybe<Scalars['String']>;
};

type IBoxLabelLotAttributeVal = { id: number; lotAttributeTitle: string };

type IDispatchBoxLabelInstructionWithVal = IDispatchBoxLabelInstructionValues & {
  lotAttributeVal: Maybe<IBoxLabelLotAttributeVal>;
};

type AssemblyOrderReceiptFormPropsType = IAssembly & { boxLabels: Array<any> };

const ReceiveModal = ({
  modalOpen,
  setModalOpen,
  onClose,
}: {
  modalOpen: boolean;
  setModalOpen: (modalOpen: boolean) => void;
  onClose: (stockAdjustmentId: string) => void;
}) => {
  const connectedForm = useContext(ConnectedFormContext);
  const [assemblyReceipt, setAssemblyReceipt] = useState<IAssemblyReceiptInput | undefined>(undefined);
  const [goodsReceipt, setGoodsReceipt] = useState<IGoodsReceiptInput | undefined>(undefined);
  const [binLocation, setBinLocation] = useState<IBinLocation | undefined>(undefined);
  const [showBoxLabels, setShowBoxLabels] = useState(false);
  const [boxLabels, setBoxLabels] = useState<Array<IDispatchBoxLabelInstructionWithVal>>([]);
  const [lotAttributeOptions, setLotAttributeOptions] = useState<Array<IAllocationLookup>>([]);
  const [productDP, setProductDP] = useState<number>(0);
  const [completeAssembly, setCompleteAssembly] = useState<boolean>(false);
  const [discardStock, setDiscardStock] = useState<boolean>(false);

  const productId = (connectedForm?.values as IAssembly)?.product?.id;

  const { data: productData } = useQuery<IProductQueryResult, IProductQueryVariables>(Product.instance!, {
    variables: {
      id: productId,
    },
    skip: !productId,
  });

  const [queryReceipt] = useLazyQuery<IAssemblyReceiptResponseQueryResult, IAssemblyReceiptResponseQueryVariables>(
    receiptResponse
  );

  const [printLabelsModalOpen, setPrintLabelsModalOpen] = useState(false);

  const [receiveAssemblies] = useBackgroundMutation<IAssemblyReceiveMutationResult, IAssemblyReceiveMutationVariables>(
    AssemblyReceiveMutation,
    {
      jobTitle: 'Receiving Assemblies',
      onFailedJob: () => {},
      onCompleteJob: (job: IJobMeta) => {
        const stockAdjustmentIds = job.properties?.stockadjustments ?? '';
        const stockAdjustmentList = stockAdjustmentIds.split(',');
        //we're only expecting to handle one stock adjustment because you can only reserve stock for an assembly from the
        //warehouse associated with the assembly
        onClose(stockAdjustmentList[0]);
        connectedForm?.reloadData?.(true);

        //if we haven't done anything
        if (job.recordsProcessed <= 0) {
          return;
        }

        const { failed, properties } = job;

        if (!failed && properties?.receiptResponseUuid) {
          //fetch the response
          queryReceipt({ variables: { uuid: properties.receiptResponseUuid } }).then((result) => {
            const { data } = result?.data ?? {};
            //if we have some actions, loop through them and set any modals that need opening to true
            if (data?.goodsReceipt) {
              //update goods receipt here
              const normalizedGoodsReceipt: IGoodsReceiptInput = normalizeVariables(data?.goodsReceipt, ['__typename']);
              setGoodsReceipt(normalizedGoodsReceipt);
            }

            //using 'some' here, will need to change for a switch if we start defining more actions
            if (data?.entityMetaData?.actions?.some((action) => action?.actionType === 'PRINT_RECEIPT_LABELS')) {
              setPrintLabelsModalOpen(true);
            } else {
              setPrintLabelsModalOpen(false);
            }
          });
        }
      },
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          assemblyReceipt,
        },
      },
    }
  );

  const optionRenderer = ({ option }: any) => {
    return option?.lotAttributeTitle ?? 'No Allocations';
  };

  const BOX_LABEL_COLUMNS = [
    {
      name: 'Box Quantity',
      key: 'boxQuantity',
      editable: true,
      formatter: 'numeric',
      options: {
        format: { decimals: 0 },
      },
      widthFactor: 1,
    },
    {
      name: 'Qty Per Box',
      key: 'quantityPerBox',
      editable: true,
      formatter: 'numeric',
      options: {
        format: { decimals: productDP },
      },
      widthFactor: 1,
    },
  ];

  const BOX_LABEL_COLUMNS_LOT_ATTRIBUTES = [
    {
      name: 'Lot Attribute',
      key: 'lotAttributeVal',
      editor: <DropdownEditor />,
      options: {
        dropdown: {
          valueKey: 'id',
          labelKey: 'lotAttributeTitle',
          options: () => lotAttributeOptions,
          optionRenderer: optionRenderer,
          clearable: false,
        },
      },
      formatter: 'dropdown',
      widthFactor: 2,
    },
    {
      name: 'Box Quantity',
      key: 'boxQuantity',
      editable: true,
      formatter: 'numeric',
      options: {
        format: { decimals: 0 },
      },
      widthFactor: 1,
    },
    {
      name: 'Qty Per Box',
      key: 'quantityPerBox',
      editable: true,
      formatter: 'numeric',
      options: {
        format: { decimals: productDP },
      },
      widthFactor: 1,
    },
  ];

  const values = connectedForm?.values as AssemblyOrderReceiptFormPropsType;

  //setup initial values
  useEffect(() => {
    const productWarehouseDetails = productData?.data?.productWarehouseDetails ?? [];
    const warehouseDetails = productWarehouseDetails.find(
      (productWarehouseDetails) => productWarehouseDetails?.warehouse?.id === values?.warehouse?.id
    );
    const defaultBinLocation = warehouseDetails?.defaultBinLocation ?? null;
    if (defaultBinLocation) {
      setBinLocation(defaultBinLocation as IBinLocation);
    }

    setAssemblyReceipt({
      ...assemblyReceipt,
      assemblyId: values.id,
      quantity: values.quantity - values.received,
      completeAssembly: completeAssembly,
      discardUnusedStock: discardStock,
      binLocationId: defaultBinLocation?.id,
    });
  }, [connectedForm.values]);

  const valid = useMemo(() => {
    if (!(assemblyReceipt?.quantity && assemblyReceipt?.binLocationId)) {
      return false;
    }

    // if box labels aren't required then pass validation
    if (!productData?.data?.boxLabelsRequired) {
      return true;
    }

    // ensure that we have enough box lables to cover the receiving amount
    const totalProductBoxQty = boxLabels
      .map((boxLabel) => (boxLabel?.boxQuantity ?? 0) * (boxLabel?.quantityPerBox ?? 0))
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0);

    return totalProductBoxQty === Number(assemblyReceipt?.quantity);
  }, [assemblyReceipt?.quantity, assemblyReceipt?.binLocationId]);

  useEffect(() => {
    setAssemblyReceipt((prevAssemblyReceipt) => ({
      ...prevAssemblyReceipt,
      completeAssembly,
      discardUnusedStock: discardStock,
    }));
  }, [completeAssembly, discardStock]);

  useEffect(() => {
    setShowBoxLabels(productData?.data?.boxLabelsRequired ?? false);
    setProductDP(productData?.data?.precision ?? 0);

    const noLotData: IAllocationLookup = {
      id: 0,
      lotAttributeTitle: 'None',
    };

    //filter out any generated lot attributes - these values are not generated yet so cannot be used to make Box Labels
    const filteredGeneratedLots = productData?.data?.lotAttributes?.filter((lotAttribute) => !lotAttribute?.generated);

    const lotData: Array<IAllocationLookup> =
      filteredGeneratedLots?.map((lotAttribute) => {
        const lotAllocation: IAllocationLookup = {
          id: lotAttribute?.id,
          lotAttributeTitle: lotAttribute?.title ?? '',
          lotInfo: null,
        };
        return lotAllocation;
      }) ?? [];

    setLotAttributeOptions([noLotData, ...lotData]);
  }, [productData]);

  const receive = () => {
    const boxLabelInstructions = boxLabels.map((boxLabel) => {
      const lotAttributeValue: any =
        assemblyReceipt?.lotAttributeValues?.find(
          (value) => value?.attributeConfig?.id === boxLabel.lotAttributeVal?.id
        ) ?? null;
      const boxLabelInstruction: IBoxLabelInstructionInput = {
        boxQuantity: boxLabel.boxQuantity,
        quantityPerBox: boxLabel.quantityPerBox,
        lotAttributeConfigTitle:
          boxLabel?.lotAttributeVal?.lotAttributeTitle === 'None'
            ? undefined
            : boxLabel?.lotAttributeVal?.lotAttributeTitle,
        lotAttributeValue:
          lotAttributeValue?.textValue ||
          lotAttributeValue?.integerValue ||
          lotAttributeValue?.doubleValue ||
          lotAttributeValue?.dateValue,
        product: normalizeVariables(values.product, ['__typename']),
      };
      return boxLabelInstruction;
    });

    if (assemblyReceipt) {
      assemblyReceipt.boxLabelInstructions = boxLabelInstructions;
    }

    receiveAssemblies();
    setModalOpen(false);
  };

  const rowUpdatedBoxLabel = (commit: ICommit<IDispatchBoxLabelInstructionWithVal>) => {
    if (!commit.updated) {
      return;
    }

    const currentBoxLabels = [...boxLabels];
    //if this is a new label, add a row
    if (commit.rowIdx >= currentBoxLabels.length) {
      const dispatchBoxLabel: IBoxLabelInstructionInput = {
        boxQuantity: 'boxQuantity' in commit.updated ? Number(commit.updated.boxQuantity) : 0,
        quantityPerBox: 'quantityPerBox' in commit.updated ? Number(commit.updated.quantityPerBox) : 0,
      };

      let lotAttributeVal: Maybe<IBoxLabelLotAttributeVal> = null;

      if (lotAttributeOptions.length > 0) {
        lotAttributeVal =
          'lotAttributeVal' in commit.updated
            ? {
                id: commit.updated.lotAttributeVal?.id || lotAttributeOptions[0].id,
                lotAttributeTitle:
                  commit.updated.lotAttributeVal?.lotAttributeTitle || lotAttributeOptions[0].lotAttributeTitle,
              }
            : { id: lotAttributeOptions[0].id, lotAttributeTitle: lotAttributeOptions[0].lotAttributeTitle };
      }

      currentBoxLabels[commit.rowIdx] = {
        ...dispatchBoxLabel,
        lotAttributeVal: lotAttributeVal,
      };
    } else {
      if ('quantityPerBox' in commit.updated) {
        currentBoxLabels[commit.rowIdx].quantityPerBox = Number(commit.updated.quantityPerBox);
      }
      if ('boxQuantity' in commit.updated) {
        currentBoxLabels[commit.rowIdx].boxQuantity = Number(commit.updated.boxQuantity);
      }
      if ('lotAttributeVal' in commit.updated) {
        currentBoxLabels[commit.rowIdx].lotAttributeVal = commit.updated.lotAttributeVal;
      }
    }
    setBoxLabels(currentBoxLabels);
  };

  //memoise the closing of the modal so that the function is not generated on every render
  const closePrintLabelsModal = useMemo(() => {
    return {
      onClose: () => {
        setBoxLabels([]);
        setPrintLabelsModalOpen(false);
      },
    };
  }, []);

  return (
    <>
      <ModalContext.Provider value={closePrintLabelsModal}>
        <ReceiptLabelModal values={goodsReceipt} isOpen={printLabelsModalOpen} />
      </ModalContext.Provider>
      <FluentModal
        isOpen={modalOpen}
        styles={{ main: { width: '50vw', paddingBottom: '60px' }, scrollableContent: { maxHeight: '97vh' } }}
      >
        <BasicHeader>Receive Assembly</BasicHeader>
        <Form.Form>
          {values?.componentReturns
            ?.filter((componentReturn) => (componentReturn?.product?.lotAttributes ?? []).length > 0)
            .map((componentReturn, index) => (
              <>
                <Form.Legend
                  label={`Required Lot Attributes for ${componentReturn?.product?.description} Component Return`}
                />
                <AttributeArray
                  data-testid={`lotAttributes-${index}`}
                  key={componentReturn?.id}
                  type="lot"
                  data={componentReturn?.product?.lotAttributes ?? []}
                  value={assemblyReceipt?.returnIdMappedLotAttributeValues?.[componentReturn?.id]}
                  onChange={(values: ILotAttributeValue[]) => {
                    const lotAttributeValuesForProduct = normalizeVariables(values, ['type']);
                    const returnId = componentReturn?.id;
                    let returnIdMappedLotAttributeValues = assemblyReceipt?.returnIdMappedLotAttributeValues ?? {};
                    returnIdMappedLotAttributeValues[returnId] = lotAttributeValuesForProduct;
                    setAssemblyReceipt((oldReceipt) => ({
                      ...oldReceipt,
                      returnIdMappedLotAttributeValues: returnIdMappedLotAttributeValues,
                    }));
                  }}
                />
              </>
            ))}
          <Form.Legend label={'Receive Options'} />
          <Form.Row>
            <Form.Fieldset cols={1}>
              <BlurTextField
                data-testid="receiveQuantity"
                label="Quantity"
                type={BlurTextFieldType.Number}
                decimals={values?.product?.precision}
                value={assemblyReceipt?.quantity}
                onChange={(value: number) => {
                  setAssemblyReceipt({ ...assemblyReceipt, quantity: value });
                }}
                enableOnChange
                mandatory
              />
              <QueryDropdown
                {...BinLocation.dropdown}
                label="Bin Location"
                predefinedFilters={[
                  {
                    filterParameters: {
                      field: 'warehouse.id',
                      value: values?.warehouse?.id,
                    },
                    filterOperation: FilterOperation.Equals,
                  },
                ]}
                value={binLocation}
                onChange={(value: IBinLocation) => {
                  setBinLocation(value);
                  setAssemblyReceipt({
                    ...assemblyReceipt,
                    binLocationId: value?.id,
                  });
                }}
                data-testid="binlocation"
                mandatory
              />
              <Toggle
                checked={completeAssembly}
                onText="Assembly Will Always Be Marked Complete"
                offText="Assembly Will Only Be Marked Complete if Fully Received"
                onChange={(event, checked: boolean | undefined) => {
                  setCompleteAssembly(checked ?? false);
                  if (!checked) {
                    setDiscardStock(false);
                  }
                }}
              />
              <Toggle
                checked={discardStock}
                onText="Unused Stock Will Be Discarded (Will Raise Stock Adjustment)"
                offText="Unused Stock Will Be Returned to Free Stock"
                onChange={(event, checked: boolean | undefined) => {
                  setDiscardStock((checked && completeAssembly) ?? false);
                }}
              />
              <AttributeArray
                type="lot"
                value={assemblyReceipt?.lotAttributeValues ?? []}
                data={productData?.data?.lotAttributes}
                label="Lot attributes"
                onChange={(values: ILotAttributeValue[]) => {
                  setAssemblyReceipt({
                    ...assemblyReceipt,
                    lotAttributeValues: normalizeVariables(values, ['type']),
                  });
                }}
                data-testid="lotAttributes"
                shouldDisplayAttributeReadOnly={(lotAttribute: ILotAttributeValue) =>
                  !lotAttribute.attributeConfig?.generated
                }
              />
            </Form.Fieldset>
          </Form.Row>
          {showBoxLabels ? (
            <Form.Row>
              <Form.Fieldset cols={1}>
                <Form.Legend label="Box Labels" />
                <EditableDataGrid
                  data-testid="boxLabelInstructions"
                  enableCellSelect={true}
                  columns={
                    (productData?.data?.lotAttributes?.length || 0) > 0
                      ? BOX_LABEL_COLUMNS_LOT_ATTRIBUTES
                      : BOX_LABEL_COLUMNS
                  }
                  listData={boxLabels}
                  addBlankRow={true}
                  rowsCount={boxLabels.length + 1}
                  onRowUpdated={rowUpdatedBoxLabel}
                />
              </Form.Fieldset>
            </Form.Row>
          ) : null}
        </Form.Form>
        <ModalBottomButtonContainer>
          <Button data-testid="shortage-generation-modal-cancel" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <SuccessButton data-testid="shortage-generation-modal-generate" onClick={receive} disabled={!valid} autoFocus>
            Receive
          </SuccessButton>
        </ModalBottomButtonContainer>
      </FluentModal>
    </>
  );
};

const useCompleteAssemblyAction = (): [IFormAction<AssemblyFormAction>, ReactElement] => {
  const connectedForm = useContext(ConnectedFormContext);
  const [completeModalOpen, setCompleteModalOpen] = useState<boolean>(false);
  const [currentStockAdjustmentUuid, setCurrentStockAdjustmentUuid] = useState<string>('');
  const [discardStock, setDiscardStock] = useState<boolean>(false);
  const [stockAdjustmentModalOpen, setStockAdjustmentModalOpen] = useState<boolean>(false);

  const assembly = connectedForm.values as IAssembly;

  const [completeAssemblyMutation] = useBackgroundMutation<
    IAssemblyCompleteMutationResult,
    IAssemblyCompleteMutationVariables
  >(AssemblyCompleteMutation, {
    jobTitle: 'Creating Assemblies',
    onCompleteJob: (job: IJobMeta) => {
      const stockAdjustmentIds = job.properties?.stockadjustments ?? '';
      const stockAdjustmentList = stockAdjustmentIds.split(',');
      const stockAdjustmentId = stockAdjustmentList[0];
      //we're only expecting to handle one stock adjustment because you can only reserve stock for an assembly from the
      //warehouse associated with the assembly
      setCurrentStockAdjustmentUuid(stockAdjustmentId);

      //if stockAdjustmentId does not equal "", then we don't need to open the stock adjustment modal
      if (stockAdjustmentId !== '') {
        setStockAdjustmentModalOpen(true);
      }
      connectedForm?.reloadData?.(true);
    },
    variables: {
      input: {
        clientMutationId: getClientMutationId(),
        assemblyCompleteRequest: {
          assemblyId: assembly.id,
          discardUnusedStock: discardStock,
        },
      },
    },
  });

  const completeClickHandler = () => {
    completeAssemblyMutation();
    setCompleteModalOpen(false);
  };

  return [
    {
      key: 'completeAssembly',
      text: 'Complete Assembly',
      onClick: () => setCompleteModalOpen(true),
      actionReference: AssemblyFormAction.CompleteAssembly,
      condition: () => assembly?.id && assembly?.status === AssemblyStatus.PartComplete,
    },
    <>
      <FluentModal
        isOpen={completeModalOpen}
        styles={{
          main: { minWidth: '25vw', maxWidth: '94vw', paddingBottom: '60px' },
          scrollableContent: { maxHeight: '50vh' },
        }}
      >
        <BasicHeader>Complete Assembly</BasicHeader>
        <Form.Form>
          <Form.Row>
            <Form.Fieldset cols={1}>
              <Toggle
                checked={discardStock}
                onText="Discard Unused Stock (Will Raise Stock Adjustment)"
                offText="Put Unused Stock Back into Stock"
                onChange={(_, checked: boolean | undefined) => {
                  setDiscardStock(checked ?? false);
                }}
              />
            </Form.Fieldset>
          </Form.Row>
        </Form.Form>

        <ModalBottomButtonContainer>
          <Button data-testid="shortage-generation-modal-cancel" onClick={() => setCompleteModalOpen(false)}>
            Cancel
          </Button>
          <SuccessButton
            data-testid="shortage-generation-modal-generate"
            onClick={completeClickHandler}
            disabled={false}
            autoFocus
          >
            Complete
          </SuccessButton>
        </ModalBottomButtonContainer>
      </FluentModal>
      <SupplierReturnStockAdjustmentModal
        open={stockAdjustmentModalOpen}
        onClose={() => setStockAdjustmentModalOpen(false)}
        uuid={currentStockAdjustmentUuid}
      />
    </>,
  ];
};

const useReceiveAssemblyAction = (): [IFormAction<AssemblyFormAction>, ReactElement] => {
  const connectedForm = useContext(ConnectedFormContext);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [stockAdjustmentModalOpen, setStockAdjustmentModalOpen] = useState<boolean>(false);
  const [currentStockAdjustmentUuid, setCurrentStockAdjustmentUuid] = useState<string>('');

  const assembly = connectedForm?.values as IAssembly;
  return [
    {
      key: 'receiveAssembly',
      text: 'Receive',
      onClick: () => setReceiveModalOpen(true),
      actionReference: AssemblyFormAction.ReceiveAssembly,
      condition: () =>
        assembly?.id && assembly?.status !== AssemblyStatus.Unprocessed && assembly?.status !== AssemblyStatus.Complete,
    },
    <>
      <ReceiveModal
        modalOpen={receiveModalOpen}
        setModalOpen={setReceiveModalOpen}
        onClose={(stockAdjustmentId: string) => {
          setCurrentStockAdjustmentUuid(stockAdjustmentId);

          //if stockAdjustmentId does not equal "", then we don't need to open the stock adjustment modal
          if (stockAdjustmentId !== '') {
            setStockAdjustmentModalOpen(true);
          }
        }}
      />
      <SupplierReturnStockAdjustmentModal
        open={stockAdjustmentModalOpen}
        onClose={() => setStockAdjustmentModalOpen(false)}
        uuid={currentStockAdjustmentUuid}
      />
    </>,
  ];
};

const usePrintAssemblyPickingListAction: () => [IFormAction<AssemblyFormAction>, ReactElement] = () => {
  const { values } = useContext(ConnectedFormContext);
  const { printDocumentSingle } = useP4DocumentController();
  const assembly = values as IAssembly;

  return [
    {
      key: 'printPickingList',
      text: 'Print Picking Instruction',
      onClick: () => {
        printDocumentSingle(ReportType.AssemblyPickingList, assembly.id, {
          metaData: { source: AssemblyPrintSource.Assembly },
        });
      },
      actionReference: AssemblyFormAction.PrintPickingList,
      condition: () => assembly?.id && assembly?.status !== AssemblyStatus.Complete,
    },
    <></>,
  ];
};

export const useAssemblyFormActions: () => [Array<IFormAction<AssemblyFormAction>>, ReactElement] = () => {
  const history = useHistory();
  const connectedForm = useContext(ConnectedFormContext);
  const [receiveAssemblyAction, receiveAssemblyModal] = useReceiveAssemblyAction();
  const [completeAssemblyAction, completeAssemblyModal] = useCompleteAssemblyAction();
  const [printPickingListAction, printPickingListModal] = usePrintAssemblyPickingListAction();
  const [performSaveThenAssignToSales] = usePerformSave(() => {
    history.push({
      pathname: `/Products/Assembly/Assignment/Details/${connectedForm.values?.id}`,
      state: { previousLocation: history.location?.pathname || 'back' },
    });
  });

  const assembly = connectedForm?.values as IAssembly;

  return [
    [
      {
        key: 'reserveComponents',
        text: 'Reserve Components',
        actionReference: AssemblyFormAction.ReserveComponents,
        onClick: () => {
          history.push({
            pathname: `/Products/Assembly/Reservation/${connectedForm.values?.id}`,
            state: { previousLocation: history.location?.pathname || 'back' },
          });
        },
        condition: () => assembly?.id && assembly?.status !== AssemblyStatus.Complete && !assembly?.archived,
      },
      {
        key: 'assignToSales',
        text: 'Assign to Sales',
        actionReference: AssemblyFormAction.AssignToSalesOrder,
        onClick: () => performSaveThenAssignToSales({ submitMeta: { suppressAfterSubmit: true } }),
        condition: () => !assembly?.archived && assembly?.id && assembly?.status !== AssemblyStatus.Complete,
        'data-testid': 'assembly-action-assign-to-sales',
      },
      receiveAssemblyAction,
      printPickingListAction,
      completeAssemblyAction,
    ],
    <>
      {receiveAssemblyModal}
      {printPickingListModal}
      {completeAssemblyModal}
    </>,
  ];
};
